import { type ReactNode, useRef } from 'react';
import { useScrollToTopOnPageChange } from '../../hooks/useScrollToTopOnPageChange.js';
import { Pagination } from './Pagination.js';
import { Spinner } from './Spinner.js';

export interface Column<T> {
  key: string;
  header: string;
  render?: (row: T) => ReactNode;
  className?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  emptyMessage?: string;
  keyExtractor: (row: T) => string;
  // 'cards' (default) stacks rows as cards below `lg` (Tailwind default, 1024px —
  // no override in this repo); 'scroll' opts a dense/numeric table out of the
  // card view and keeps the single horizontally-scrolling table at all widths.
  mobile?: 'cards' | 'scroll';
  // 037: optional multi-select. When `selectable`, a leading checkbox column is
  // rendered (header checkbox = select-all-on-page, indeterminate when only part
  // of the page is selected) and each card in the mobile view gets a checkbox.
  // Selection STATE lives in the caller; the table only reflects + reports.
  selectable?: boolean;
  selectedIds?: Set<string>;
  onToggleRow?: (id: string) => void;
  onToggleAllPage?: () => void;
  // 063 (gap-list H11): what the query returned, when it did not return rows.
  // Structural rather than `ApiError` so this presentational component stays
  // independent of the transport layer and a test can hand it a plain object.
  //
  // Additive: omit it and the table behaves exactly as it did before 063, which
  // is what let the pages migrate one at a time.
  error?: { status?: number } | null;
  // Per-screen refusal wording. Defaults to REFUSED_MESSAGE below.
  refusedMessage?: string;
  /**
   * 163: the list's own pager, rendered ABOVE and BELOW the rows.
   *
   * The table owns this rather than each screen pasting two `<Pagination>` elements, so a list
   * screen written later gets both without its author needing to know — the same
   * correct-by-construction argument feature 161 made about invalidation. Both instances read this
   * one `page` and call this one handler, so they cannot disagree about which page is current.
   *
   * Optional, and when absent the markup is byte-identical to what this component rendered before
   * 163 — which is what let the 15 screens migrate one at a time.
   */
  pagination?: {
    page: number;
    totalPages: number;
    onPageChange: (page: number) => void;
  };
}

const checkboxClass = 'h-4 w-4 accent-primary cursor-pointer';

const REFUSED_MESSAGE = 'This section is not available for your account.';
const FAILED_MESSAGE = 'This section could not be loaded. Please try again shortly.';

/**
 * What to say when there are no rows to show.
 *
 * A refusal, a failure and a genuinely empty list are three different facts.
 * Until 063 all three rendered `emptyMessage`, so a provider refused
 * `/suppliers` was told "No suppliers yet" while two organisations existed —
 * a confident falsehood, which is worse than an error.
 *
 * The refusal/failure split matters just as much: claiming "not available for
 * your account" after a 500 asserts a permission fact that may well be false,
 * and buries an outage under a reassuring sentence. Same reasoning as
 * DashboardPage's own branch.
 */
function emptyStateText(
  error: { status?: number } | null | undefined,
  emptyMessage: string,
  refusedMessage: string | undefined,
): string {
  if (!error) return emptyMessage;
  const refused = error.status === 403 || error.status === 401;
  return refused ? (refusedMessage ?? REFUSED_MESSAGE) : FAILED_MESSAGE;
}

export function DataTable<T>({
  columns,
  data,
  loading,
  emptyMessage = 'No records found',
  keyExtractor,
  mobile = 'cards',
  selectable = false,
  selectedIds,
  onToggleRow,
  onToggleAllPage,
  error,
  refusedMessage,
  pagination,
}: DataTableProps<T>) {
  /**
   * 163: the element the pagers and the rows share, and the anchor the scroll hook walks up from
   * to find the real scrolling container. It must wrap the rows, not sit beside them.
   */
  const listRef = useRef<HTMLDivElement | null>(null);
  useScrollToTopOnPageChange(listRef, pagination?.page ?? 1);

  /** Wraps whichever body this component rendered in its two pagers. */
  function withPagination(body: ReactNode): ReactNode {
    if (!pagination) return body;
    return (
      <div ref={listRef}>
        <Pagination {...pagination} position="top" />
        {body}
        <Pagination {...pagination} position="bottom" />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  // One computation, used by BOTH empty regions below (the table cell and the
  // mobile card). Fixing only the table would hold on desktop and fail on the
  // layout a provider on a phone actually sees.
  const noRowsText = emptyStateText(error, emptyMessage, refusedMessage);

  const selected = selectedIds ?? new Set<string>();
  const allOnPageSelected = data.length > 0 && data.every((row) => selected.has(keyExtractor(row)));
  const someOnPageSelected = data.some((row) => selected.has(keyExtractor(row)));

  const rowCheckbox = (row: T) => (
    <input
      type="checkbox"
      aria-label="Select row"
      className={checkboxClass}
      checked={selected.has(keyExtractor(row))}
      onChange={() => onToggleRow?.(keyExtractor(row))}
    />
  );

  const table = (
    <div
      data-testid="datatable-table"
      className={[
        'overflow-x-auto rounded-xl shadow-[0px_1px_2px_0px_rgba(0,30,43,0.04)] border border-hairline',
        mobile === 'cards' ? 'hidden lg:block' : '',
      ].join(' ')}
    >
      <table className="min-w-full">
        <thead className="bg-surface border-b border-hairline">
          <tr>
            {selectable && (
              <th className="w-10 px-4 py-3">
                <input
                  type="checkbox"
                  aria-label="Select all on page"
                  className={checkboxClass}
                  checked={allOnPageSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someOnPageSelected && !allOnPageSelected;
                  }}
                  onChange={() => onToggleAllPage?.()}
                />
              </th>
            )}
            {columns.map((col) => (
              <th
                key={col.key}
                className={[
                  'px-4 py-3 text-left text-xs font-medium text-steel',
                  col.className ?? '',
                ].join(' ')}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-canvas">
          {data.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length + (selectable ? 1 : 0)}
                className="px-4 py-8 text-center text-sm text-steel"
              >
                {noRowsText}
              </td>
            </tr>
          ) : (
            data.map((row) => (
              <tr key={keyExtractor(row)} className="hover:bg-surface transition-colors">
                {selectable && <td className="w-10 px-4 py-3.5">{rowCheckbox(row)}</td>}
                {columns.map((col) => (
                  <td key={col.key} className="px-4 py-3.5 text-sm text-ink">
                    {col.render
                      ? col.render(row)
                      : String((row as Record<string, unknown>)[col.key] ?? '')}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  if (mobile === 'scroll') {
    return withPagination(table);
  }

  return withPagination(
    <>
      <div data-testid="datatable-cards" className="lg:hidden space-y-3">
        {data.length === 0 ? (
          <div className="rounded-xl shadow-[0px_1px_2px_0px_rgba(0,30,43,0.04)] border border-hairline bg-canvas px-4 py-8 text-center text-sm text-steel">
            {noRowsText}
          </div>
        ) : (
          data.map((row) => (
            <div
              key={keyExtractor(row)}
              // Cards are the only place row actions render below `lg`; widen their tap
              // area here since `col.render` output is opaque (a Link/button we don't
              // control per-column) — desktop is unaffected, this block never renders there.
              className="space-y-2 rounded-xl shadow-[0px_1px_2px_0px_rgba(0,30,43,0.04)] border border-hairline bg-canvas p-4 [&_a]:inline-flex [&_a]:min-h-11 [&_a]:items-center [&_a]:py-1 [&_button]:inline-flex [&_button]:min-h-11 [&_button]:items-center [&_button]:py-1"
            >
              {selectable && (
                <div className="flex items-center justify-between gap-4 text-sm">
                  <span className="font-semibold text-steel">Select</span>
                  {rowCheckbox(row)}
                </div>
              )}
              {columns.map((col) => {
                const value = col.render
                  ? col.render(row)
                  : String((row as Record<string, unknown>)[col.key] ?? '');
                return (
                  <div key={col.key} className="flex items-center justify-between gap-4 text-sm">
                    {col.header ? (
                      <span className="font-semibold text-steel">{col.header}</span>
                    ) : null}
                    <span className={col.header ? 'text-ink text-right' : 'text-ink'}>{value}</span>
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>
      {table}
    </>,
  );
}
